import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { APP_CONTENT_QUERY_KEYS } from '@/lib/constants/appContent';
import { AppContentApi } from './AppContentApi';
import type { ListlistCreateReq, ListlistUpdateReq } from '@lib2/model/listlist/ListlistReq';
import type { ChildListlistOrListId } from '@lib2/model/listlist/ListlistReq';

// ── Listlist CRUD ──

export function useCreateListlist() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: ListlistCreateReq) =>
      AppContentApi.createListlist(selectedCId!, req),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: APP_CONTENT_QUERY_KEYS.listlists(selectedCId!),
      });
    },
  });
}

export function useUpdateListlist(llId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: ListlistUpdateReq) =>
      AppContentApi.updateListlist(selectedCId!, llId, req),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: APP_CONTENT_QUERY_KEYS.listlists(selectedCId!),
      });
      queryClient.invalidateQueries({
        queryKey: APP_CONTENT_QUERY_KEYS.detail(selectedCId!, llId),
      });
    },
  });
}

export function useDeleteListlist() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (llId: number) =>
      AppContentApi.deleteListlist(selectedCId!, llId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: APP_CONTENT_QUERY_KEYS.listlists(selectedCId!),
      });
      queryClient.invalidateQueries({
        queryKey: APP_CONTENT_QUERY_KEYS.positions(selectedCId!),
      });
    },
  });
}

// ── Position Mutations (Hierarchy) ──

export function useAddChild() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      parentLLId,
      childId,
    }: {
      parentLLId: number;
      childId: ChildListlistOrListId;
    }) =>
      AppContentApi.createPosition(selectedCId!, { parentLLId, childId }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: APP_CONTENT_QUERY_KEYS.positions(selectedCId!),
      });
    },
  });
}

export function useRemoveChild() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      parentLLId,
      childId,
    }: {
      parentLLId: number;
      childId: ChildListlistOrListId;
    }) =>
      AppContentApi.deletePosition(selectedCId!, { parentLLId, childId }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: APP_CONTENT_QUERY_KEYS.positions(selectedCId!),
      });
    },
  });
}

export function useSaveChildOrder() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      parentLLId,
      childIds,
    }: {
      parentLLId: number;
      childIds: ChildListlistOrListId[];
    }) =>
      AppContentApi.updatePositions(selectedCId!, { parentLLId, childIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: APP_CONTENT_QUERY_KEYS.positions(selectedCId!),
      });
    },
  });
}
